import type { RouteSeo, SeoImage, SeoSite } from '@kkf/design'
import { EVENT_ENTRIES } from './events'

/**
 * The Foundation's site tree — what a search engine is told about every route.
 *
 * ## This file is loaded by Node, so it may hold no value import from `@kkf/design`
 *
 * `vite.config.ts` imports it so the build can write each route's metadata into a real
 * HTML file. Vite bundles the config with esbuild and externalises bare specifiers, so a
 * value import here (`appUrl`, `LINKS`, a component) would be handed to Node, which would
 * try to parse the `.tsx` the design system's barrel re-exports and fail the whole build
 * with a JSX syntax error. `import type` is erased and is therefore fine, as is a relative
 * import of another data module that follows the same rule — which is why the events below
 * can be read straight out of the file that defines them. The donation causes could too,
 * until 2026-09-25; see `DONATION_ROUTES` for why they no longer can.
 *
 * The same list is handed to `AppShell` in `App.tsx`, so the head a crawler is served and
 * the head a visitor navigates into come from one place.
 *
 * ## Why several routes are `noindex`
 *
 * `/team`, `/pricing`, `/faq` and the two template detail pages still carry the purchased
 * template's content: eight people called John Doe, three plans at $19/$59/$110, and
 * three accordion answers of lorem ipsum. A crawler cannot tell placeholder copy from
 * real copy — it indexes it, ranks the site on it, and shows it to somebody searching for
 * the Foundation. Each is an open blocker on `foundation-website-prd.md`; take the
 * `noindex` off the same day the content lands.
 */

/**
 * Photographs used as social cards, with the alt text the page itself gives them.
 *
 * Only real photographs are listed. A route whose banner is still a sized placeholder
 * falls back to `SITE.defaultImage` rather than putting a grey rectangle in a shared link
 * — and an image with no alt anywhere in the app has no honest alt to give here either.
 */
const IMAGES = {
  lodge: {
    url: '/assets/img/banner-lodge-winter.jpg',
    alt: 'A timber lodge under deep snow, backed by a snow-covered pine forest',
  },
  campus: {
    url: '/assets/img/resort-campus-aerial.jpg',
    alt:
      'The resort campus from the air — guest cottages and a domed main hall set among ' +
      'lawns, gardens and a playground, ringed by pine forest and mountains',
  },
  workshop: {
    url: '/assets/img/garage-repair-bench.jpg',
    alt: 'A mechanic bent over a stripped motorcycle, working on it by hand',
  },
  guestRoom: {
    url: '/assets/img/resort-guest-room.jpg',
    alt: 'A twin guest room with a kitchenette and an en-suite bathroom beyond',
  },
  rider: {
    url: '/assets/img/youth-rider-steppe.jpg',
    alt: 'A young man smiling astride a loaded motorcycle on the open steppe',
  },
  reading: {
    url: '/assets/img/resort-terrace-reading.jpg',
    alt: 'A man reading on a timber terrace, a mug beside him and mountains beyond',
  },
  dance: {
    url: '/assets/img/banner-community-dance.jpg',
    alt: 'Community members dancing together in traditional dress at a winter gathering',
  },
} satisfies Record<string, SeoImage>

export const SITE: SeoSite = {
  // The production host, always — see `SeoSite.origin`. Deliberately not read from
  // `VITE_FOUNDATION_URL`: that variable exists so an app can point at localhost, and a
  // localhost canonical is a bug that only shows up once it is in the index.
  origin: 'https://kkfmongolia.com',
  name: 'King of Kings Foundation',
  locale: 'en_US',
  defaultImage: IMAGES.lodge,
  schema: {
    organization: {
      type: 'NGO',
      name: 'King of Kings Foundation',
      description:
        'A Christian foundation in Mongolia, accompanying people toward a closer relationship with God through mentoring, discipleship and places of rest.',
      logo: '/assets/img/logo.png',
      // The one real profile. The template shipped four placeholder icons pointing at `#`;
      // `sameAs` is how a search engine connects this site to a page it already knows, so
      // a wrong entry here is worse than a short list.
      sameAs: ['https://www.facebook.com/profile.php?id=61592414898013'],
      // Real and monitored, confirmed 2026-09-03. Each venture has its own — `garage@`,
      // `resort@`, `partners@` — so a search result, a footer and a knowledge panel all
      // point at the inbox that can answer them. It is the Foundation's only working
      // contact channel, which is why it is also first on `/contact`.
      email: 'foundation@kkfmongolia.com',
      areaServed: 'Mongolia',
      // Locality and country, and no `streetAddress`, `telephone` or `geo`: the Foundation
      // is in Ulaanbaatar and that much is confirmed, but the street address and the
      // telephone number do not exist yet. A wrong one in a knowledge panel is a wrong
      // answer somebody acts on by driving somewhere — see
      // `foundation-website-prd.md` § Blocked.
      addressLocality: 'Ulaanbaatar',
      addressCountry: 'MN',
    },
    website: {
      description:
        'The Foundation’s own site: the story, the seven areas of work, the blog and the donation ask.',
    },
  },
}

/** The pages that exist in their own right. */
export const DONATIONS_ROUTE: RouteSeo = {
  path: '/donations',
  name: 'Donate',
  title: 'Donate — give to the work in Mongolia',
  description:
    'Four causes you can give to: youth skills and the garage, mentoring and discipleship, retreats and community stays, and the in-house English library.',
  image: IMAGES.rider,
  changefreq: 'weekly',
  priority: 0.9,
}

const STATIC_ROUTES: RouteSeo[] = [
  {
    path: '/',
    name: 'Home',
    title: 'King of Kings Foundation — For Lives Worth Living',
    description:
      'A Christian foundation in Mongolia: mentoring and discipleship, a Christ-centred resort, an off-road training garage, and a Christian business partners.',
    image: IMAGES.lodge,
    changefreq: 'monthly',
    priority: 1,
  },
  {
    path: '/about',
    name: 'About',
    // The page opened on "The Need" until 2026-09-21 and the title said so. It opens on
    // what the Foundation believes now, so the title does too.
    title: 'About the Foundation — what we believe, and what we do',
    description:
      'Why King of Kings Foundation exists, what it believes about the calling on a person’s life, and the mentoring, hospitality and community it offers in Mongolia.',
    image: IMAGES.campus,
    changefreq: 'monthly',
    priority: 0.9,
  },
  {
    path: '/services',
    name: 'Our work',
    title: 'Our work — the seven areas the Foundation serves',
    description:
      'Youth mentoring and skills, a Christ-centred resort, missionary tourism, marriage and family, a business partners, holistic health, and community spaces.',
    image: IMAGES.workshop,
    changefreq: 'monthly',
    priority: 0.8,
  },
  {
    path: '/projects',
    name: 'Our places',
    title: 'Our places — resort, library, garage and Partners',
    description:
      'The four things King of Kings Foundation is building in Mongolia: the resort, the English library, King of Kings Garage and King of Kings Partners.',
    image: IMAGES.guestRoom,
    changefreq: 'monthly',
    priority: 0.8,
  },
  /*
   * Exported as well as listed, because `DonationsPage` needs it: selecting a cause puts
   * `?cause=` in the URL, and that view is the same words `/donations/<slug>` already
   * serves, so it marks itself `noindex` from the page. The bare route stays indexed and
   * stays in the sitemap. Same mechanism as the resort's `/book`.
   */
  DONATIONS_ROUTE,
  {
    path: '/events',
    name: 'Events',
    title: 'Events — the gala, the festival, and Mongolia trips',
    description:
      'The Winter Gala, the Autumn Pumpkin Festival, and personalised Mongolia trip packages — what each one is, and who it is for.',
    image: IMAGES.dance,
    changefreq: 'weekly',
    priority: 0.7,
  },
  {
    path: '/blog',
    name: 'Blog',
    title: 'Blog — stories from the Foundation’s work',
    description:
      'Stories from the resort, the garage and the volunteers: mentoring, the English library, prophetic art weekends, and a children’s book about acceptance.',
    image: IMAGES.reading,
    changefreq: 'weekly',
    priority: 0.7,
  },
  {
    path: '/contact',
    name: 'Contact',
    title: 'Contact King of Kings Foundation',
    description:
      'Get in touch about volunteering, partnership, a stay at the resort, or a question about the Foundation’s work in Mongolia.',
    changefreq: 'yearly',
    priority: 0.6,
  },

  // ── still the template's content — see the note at the top of this file ──────
  {
    path: '/team',
    name: 'Our team',
    title: 'Our team — King of Kings Foundation',
    description:
      'The people behind the Foundation’s work in Mongolia — mentors, hosts, trainers and the staff who keep the places running.',
    noindex: true,
  },
  {
    path: '/pricing',
    name: 'Plans',
    title: 'Partnership plans — King of Kings Foundation',
    description:
      'Ways to partner with the Foundation on a regular basis, and what each level of partnership supports across the work.',
    noindex: true,
  },
  {
    path: '/faq',
    name: 'Questions',
    title: 'Volunteering questions — King of Kings Foundation',
    description:
      'What volunteering with the Foundation involves, what a volunteer can expect, and how to start a conversation about it.',
    noindex: true,
  },
  {
    path: '/services/clean-water-supply',
    name: 'Service details',
    title: 'Service details — King of Kings Foundation',
    description:
      'A single area of the Foundation’s work in detail: what it is, who it serves, and how it is paid for.',
    noindex: true,
  },
  {
    path: '/team/jenny-wilson',
    name: 'Team member',
    title: 'Team member — King of Kings Foundation',
    description:
      'One member of the Foundation’s team: what they do, how long they have been part of the work, and how to reach them.',
    noindex: true,
  },
  /*
   * The two shared legal pages (2026-09-19).
   *
   * The words are `@kkf/design`'s — one privacy policy and one set of terms for all five
   * apps — but the site tree entry is each app's own, because the title, the description
   * and the canonical are about THIS host. `audit:seo` checks titles and descriptions for
   * uniqueness within an app, not across the platform, so four apps each carrying a
   * `/privacy` is correct; four identical descriptions would be four pages competing for
   * the same result, which is why each names the thing that site actually asks for.
   *
   * `priority: 0.3` and `changefreq: 'yearly'`: indexed, findable, and not competing with
   * the pages somebody came for.
   */
  {
    path: '/privacy',
    name: 'Privacy Policy',
    title: 'Privacy at King of Kings Foundation',
    description:
      'What the Foundation’s websites collect, what they never collect, and who else handles it. No analytics, no advertising, no tracking of any kind.',
    changefreq: 'yearly',
    priority: 0.3,
  },
  {
    path: '/terms',
    name: 'Terms and Conditions',
    title: 'Terms of use — King of Kings Foundation',
    description:
      'The terms that cover every King of Kings website: the Partners directory, accounts, giving, and what the Foundation can and cannot promise you.',
    changefreq: 'yearly',
    priority: 0.3,
  },
]

/**
 * The two appeals the build knows about, described by hand.
 *
 * ## Why by hand, since 2026-09-25
 *
 * The appeals moved out of `data/donations.ts` and into `app_api`, where Steward writes them.
 * This file cannot follow them there: it is loaded by **Node, at build time**, and a build that
 * fetched from the API would be a build that fails — or silently drops every appeal from the
 * sitemap — whenever the API is down, or is production while the build is a laptop's. So the
 * two appeals seeded by the migration keep a pre-rendered head and a sitemap entry, written out
 * here, and their titles and photographs are copied from the seed.
 *
 * **What that costs, named rather than hidden:** an appeal added in Steward after the last
 * build has no pre-rendered HTML and is not in the sitemap. It works at runtime — the page
 * writes its own head through `useSeo` — but a crawler that does not run JavaScript sees
 * `/donations/<slug>` with the site's default card. The fix is a build-time snapshot of
 * `GET /v1/donations/appeals` that falls back to this list, and it is a backlog entry on the
 * Foundation PRD rather than something to half-build here.
 *
 * Until then this list and the seed can drift: renaming the capital ask in Steward leaves its
 * old title in the sitemap's HTML until somebody edits this. The earlier version of this list
 * threw when a slug disagreed with `data/donations.ts`; there is nothing on disk to check
 * against any more, so it cannot.
 *
 * Descriptions are written for a search result rather than derived from the card copy: a
 * card summary is written to sit under a photograph and reads badly cut to 160 characters.
 */
const DONATION_ROUTES: RouteSeo[] = [
  {
    path: '/donations/initial-capital-investment',
    name: 'An Initial Capital Investment Of USD $1 Million',
    title: 'An Initial Capital Investment Of USD $1 Million — donate',
    description:
      'The USD $1 million to build the resort, the mentoring and community facilities, the gardens and the garage every King of Kings programme runs on.',
    image: {
      url: '/assets/img/resort-campus-aerial.jpg',
      alt: 'The resort campus from the air — guest cottages and a domed main hall set among lawns, gardens and a playground, ringed by pine forest and mountains',
    },
    changefreq: 'monthly',
    priority: 0.6,
  },
  {
    path: '/donations/shipping-the-library-books',
    name: 'Shipping The Library Books From Vietnam',
    title: 'Shipping The Library Books From Vietnam — donate',
    description:
      'USD $2,000 to fly the English collection from Vietnam to Mongolia, so the books already given to the library reach its shelves.',
    image: {
      url: '/assets/img/resort-reading-room.jpg',
      alt: 'The reading room — armchairs and filled shelves under a tall window',
    },
    changefreq: 'monthly',
    priority: 0.6,
  },
]

/** Looks an event up, failing the build rather than emitting a route with no page. */
const event = (slug: string) => {
  const found = EVENT_ENTRIES.find((entry) => entry.slug === slug)
  if (!found) throw new Error(`[seo] no event "${slug}" — data/events.ts and data/seo.ts disagree`)
  return found
}

/**
 * The three events.
 *
 * Each carries an `Event` node **without a `startDate`**. No dates have been set — the
 * cards show a season rather than an invented day — and `startDate` is what Google needs
 * for an event rich result. The node is still worth emitting: it says accurately what the
 * page is about. Add the dates and the rich result follows; invent them and the first
 * person to arrive on the wrong evening finds out.
 */
const EVENT_ROUTES: RouteSeo[] = (
  [
    {
      slug: 'winter-gala',
      description:
        'An evening of dinner, fellowship and presentations in Ulaanbaatar, bringing partners, supporters and community leaders together behind the work.',
    },
    {
      slug: 'autumn-pumpkin-festival',
      description:
        'A day of games, performances, food and community on the resort grounds, for churches, partners, businesses, schools and families.',
    },
    {
      slug: 'mongolia-trip-packages',
      description:
        'Resort relaxation, nature, cultural travel and ministry or volunteer work, tailored to your group, length of stay, interests and budget.',
    },
  ] as const
).map(({ slug, description }): RouteSeo => {
  const entry = event(slug)
  return {
    path: `/events/${slug}`,
    name: entry.title.en,
    title: entry.title.en,
    description,
    event: { name: entry.title.en, description, locationName: entry.venue.en },
    changefreq: 'monthly',
    priority: 0.6,
  }
})

/**
 * Every route this app can enumerate, except the blog.
 *
 * Blog posts are markdown read at build time through `virtual:blog-posts`, which is a
 * bundler concept and cannot be resolved by the Node process that loads this file. They
 * are appended in two places instead — `App.tsx` for the runtime and `vite.config.ts` for
 * the build — and both call `postSeo`, so there is still only one description of a post.
 * That derivation moved to `@kkf/design`'s `seo/postSeo.ts` on 2026-09-21, when the blog
 * became one library on four sites; `App.tsx` imports it from the barrel and every
 * `vite.config.ts` imports the same file by relative path, because Vite runs a config in
 * Node and the barrel re-exports JSX that esbuild cannot parse there.
 */
export const ROUTES: RouteSeo[] = [...STATIC_ROUTES, ...DONATION_ROUTES, ...EVENT_ROUTES]
